import { useFetcher } from '@remix-run/react'
import * as React from 'react'

export const cache = new Map()

export const Queries = {
	imagesList: {
		key: (page: number) => `imagesList-${page}`,
		url: (page: number) => `/my-images/load?page=${page}`,
	},
	userSavedImages: {
		key: () => 'userSavedImages',
		url: () => '/me/user-images',
	},
}

export function useCachedFetcher<T>(
	queryKey: any,
	queryUrl: string,
): ReturnType<typeof useFetcher<T>> {
	const fetcher = useFetcher<T>()
	const prevQueryKey = React.useRef('')
	const [fakeFetcher, setFakeFetcher] = React.useState(fetcher)

	React.useEffect(() => {
		if (
			fetcher.state === 'idle' &&
			fetcher.data == null &&
			!cache.get(queryKey)
		) {
			fetcher.load(queryUrl)
		}
	}, [fetcher, queryUrl, queryKey])

	React.useEffect(() => {
		if (prevQueryKey.current !== queryKey) {
			if (cache.get(queryKey)) {
				setFakeFetcher(prev => ({ ...prev, data: cache.get(queryKey) }))
			} else {
				fetcher.load(queryUrl)
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [queryUrl, queryKey])

	React.useEffect(() => {
		if (fetcher.data) {
			cache.set(queryKey, fetcher.data)
			setFakeFetcher(fetcher)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fetcher.data])

	return fakeFetcher
}

export type UpdaterFn = (data: any) => any
export function updateClientCache(queryKey: string, data: UpdaterFn) {
	const cachedData = cache.get(queryKey)
	if (cachedData) {
		cache.set(queryKey, data(cachedData))
	}
}

export function invalidateClientCache(queryKey: keyof typeof Queries) {
	cache.delete(queryKey)
}
